import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  Match,
  onCleanup,
  Show,
  Switch,
} from "solid-js";
import { Dynamic } from "solid-js/web";
import { msg } from "@lingui/core/macro";
import type { VaultItemRecord } from "@boveda/powersync";
import { crypto, getVaultItemsQuery, queryKeys } from "@boveda/shared";
import { createPowerSyncQuery } from "@boveda/shared/solid";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Contact,
  Copy,
  CreditCard,
  Eye,
  EyeOff,
  FileText,
  KeyRound,
  Pencil,
  Plus,
  Share2,
  Trash2,
  Upload,
  UserSquare,
} from "@fleet/icons";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { Button } from "@/components/ui/button";
import { TextField } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { useI18n } from "@/providers/i18n-provider";
import { type DecryptedVaultItem, useVault } from "../vault-context";
import {
  deriveInventory,
  formatTotpCode,
  type InventoryRow,
  type ItemStatus,
  type ListableItem,
  type VaultItemType,
} from "../vault-state";
import { AttachmentsPanel } from "./attachments-panel";
import { ImportExportPanel } from "./import-export-panel";
import { ItemForm } from "./item-form";
import { ShareSecretPanel } from "./share-secret-panel";

const COPIED_FEEDBACK_MS = 1500;

type ViewMode =
  | { kind: "list" }
  | { kind: "new" }
  | { kind: "view"; item: DecryptedVaultItem }
  | { kind: "edit"; item: DecryptedVaultItem }
  | { kind: "share"; item: DecryptedVaultItem }
  | { kind: "import-export" };

/** Vault main page: item list + create/view/edit. */
export function VaultPage() {
  const { t } = useI18n();
  const vault = useVault();
  const { db } = usePowerSyncContext();
  const [mode, setMode] = createSignal<ViewMode>({ kind: "list" });
  const [search, setSearch] = createSignal("");

  const itemsQuery = createPowerSyncQuery<VaultItemRecord>({
    queryKey: () => queryKeys.vault.items(vault.vaultId() ?? undefined),
    queryFn: () => {
      const drizzle = vault.drizzle();
      const vid = vault.vaultId();
      return drizzle && vid ? getVaultItemsQuery(drizzle, vid) : null;
    },
    db,
    enabled: () => Boolean(vault.vaultId()),
  });

  // Decrypts all rows in memory (page mounts only when unlocked).
  const [decrypted] = createResource(
    () => itemsQuery.data ?? [],
    async (rows) => {
      const out: DecryptedVaultItem[] = [];
      for (const row of rows) {
        try {
          out.push(await vault.decryptRow(row));
        } catch {
          // Unreadable item (wrong key / corrupted): omitted.
        }
      }
      return out;
    },
  );

  // Inventory derived from REAL STATE: list (type/name/last activity)
  // combined with LIVE health analysis (weak/reused/old) so
  // each row reflects current state, not outdated static icon. Everything
  // runs on client over already decrypted vault (zero-knowledge).
  const listItems = createMemo<InventoryRow[]>(() => {
    const decryptedItems = decrypted() ?? [];
    const items: ListableItem[] = decryptedItems.map((d) => ({
      id: d.id,
      type: d.plain.type,
      name: d.plain.name,
      username: d.plain.fields.username,
      updatedAt: d.plain.updatedAt,
    }));
    const passwordEntries = decryptedItems
      .filter((d) => d.plain.fields.password)
      .map((d) => ({
        id: d.id,
        password: d.plain.fields.password ?? "",
        updatedAt: d.plain.updatedAt,
      }));
    const report = crypto.analyzeVaultHealth(passwordEntries);
    return deriveInventory(items, report, search());
  });

  const byId = createMemo(() => new Map((decrypted() ?? []).map((d) => [d.id, d])));

  async function handleSave(
    plain: Parameters<typeof vault.saveItem>[0],
    existing?: { id: string; rev: number },
  ): Promise<void> {
    await vault.saveItem(plain, existing);
    setMode({ kind: "list" });
  }

  async function handleDelete(id: string): Promise<void> {
    await vault.deleteItem(vault.vaultId() ?? "", id);
    setMode({ kind: "list" });
  }

  // Encrypts and persists imported item batch (CSV / encrypted backup).
  async function saveManyItems(
    items: readonly Parameters<typeof vault.saveItem>[0][],
  ): Promise<number> {
    for (const plain of items) {
      await vault.saveItem(plain);
    }
    return items.length;
  }

  return (
    <div class="mx-auto w-full max-w-2xl px-4 py-6" onPointerDown={() => vault.keepAlive()}>
      <Switch>
        {/* ── Lista ─────────────────────────────────────────────── */}
        <Match when={mode().kind === "list"}>
          <div class="mb-4 flex items-center justify-between gap-3">
            <h1 class="text-xl font-semibold">{t(msg`Your vault`)}</h1>
            <div class="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode({ kind: "import-export" })}
                data-testid="vault-import-export"
              >
                <Upload class="h-4 w-4" /> {t(msg`Import / Export`)}
              </Button>
              <Button
                size="sm"
                onClick={() => setMode({ kind: "new" })}
                data-testid="vault-add-item"
              >
                <Plus class="h-4 w-4" /> {t(msg`Add item`)}
              </Button>
            </div>
          </div>

          <TextField
            class="mb-4"
            placeholder={t(msg`Search…`)}
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
            data-testid="vault-search"
          />

          <Show
            when={listItems().length > 0}
            fallback={
              <EmptyState
                icon={KeyRound}
                title={t(msg`No items yet`)}
                description={t(msg`Add your first login or secure note to get started.`)}
              />
            }
          >
            <ul class="flex flex-col gap-1.5" data-testid="vault-item-list">
              <For each={listItems()}>
                {(item) => (
                  <li>
                    <button
                      type="button"
                      class="flex w-full items-center gap-3 rounded-lg border border-border/50 bg-card/40 px-3 py-2.5 text-left transition-colors hover:bg-primary/5"
                      onClick={() => {
                        const full = byId().get(item.id);
                        if (full) {
                          setMode({ kind: "view", item: full });
                        }
                      }}
                      data-testid="vault-item"
                      data-status={item.status}
                    >
                      <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <ItemTypeIcon type={item.type} />
                      </span>
                      <span class="min-w-0 flex-1">
                        <span class="block truncate text-sm font-medium">{item.name}</span>
                        <Show when={item.username}>
                          <span class="block truncate text-xs text-muted-foreground">
                            {item.username}
                          </span>
                        </Show>
                      </span>
                      <StatusBadge status={item.status} />
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Match>

        {/* ── Crear ─────────────────────────────────────────────── */}
        <Match when={mode().kind === "new"}>
          <BackHeader label={t(msg`New item`)} onBack={() => setMode({ kind: "list" })} />
          <ItemForm onSave={handleSave} onCancel={() => setMode({ kind: "list" })} />
        </Match>

        {/* ── Editar ────────────────────────────────────────────── */}
        <Match when={mode().kind === "edit"}>
          {(_) => {
            const current = mode();
            const editing = current.kind === "edit" ? current.item : undefined;
            return (
              <>
                <BackHeader
                  label={t(msg`Edit item`)}
                  onBack={() => editing && setMode({ kind: "view", item: editing })}
                />
                <ItemForm
                  editing={editing}
                  onSave={handleSave}
                  onCancel={() => editing && setMode({ kind: "view", item: editing })}
                />
              </>
            );
          }}
        </Match>

        {/* ── Ver ───────────────────────────────────────────────── */}
        <Match when={mode().kind === "view"}>
          {(_) => {
            // oxlint-disable-next-line solid/reactivity -- intended snapshot: this Match re-runs only on a kind change, and no setMode call goes view to view
            const current = mode();
            if (current.kind !== "view") {
              return null;
            }
            // Re-reads most recent decrypted version (after edit).
            const fresh = () => byId().get(current.item.id) ?? current.item;
            return (
              <ItemDetail
                item={fresh()}
                vaultId={vault.vaultId() ?? ""}
                onBack={() => setMode({ kind: "list" })}
                onEdit={() => setMode({ kind: "edit", item: fresh() })}
                onDelete={() => handleDelete(current.item.id)}
                onShare={() => setMode({ kind: "share", item: fresh() })}
              />
            );
          }}
        </Match>

        {/* ── Share (envelope sealed to recipient public key) ── */}
        <Match when={mode().kind === "share"}>
          {(_) => {
            // oxlint-disable-next-line solid/reactivity -- intended snapshot: this Match re-runs only on a kind change, and no setMode call goes share to share
            const current = mode();
            if (current.kind !== "share") {
              return null;
            }
            return (
              <>
                <BackHeader
                  label={t(msg`Share item`)}
                  onBack={() => setMode({ kind: "view", item: current.item })}
                />
                <ShareSecretPanel
                  item={current.item}
                  onDone={() => setMode({ kind: "view", item: current.item })}
                />
              </>
            );
          }}
        </Match>

        {/* ── Import / Export (CSV + respaldo cifrado + export en claro) ── */}
        <Match when={mode().kind === "import-export"}>
          <BackHeader label={t(msg`Import / Export`)} onBack={() => setMode({ kind: "list" })} />
          <ImportExportPanel
            items={(decrypted() ?? []).map((d) => d.plain)}
            saveMany={saveManyItems}
          />
        </Match>
      </Switch>
    </div>
  );
}

const TYPE_ICONS: Record<VaultItemType, typeof KeyRound> = {
  login: KeyRound,
  note: FileText,
  card: CreditCard,
  identity: Contact,
};

/** Icon based on REAL item type (login/note/card/identity). */
function ItemTypeIcon(props: { type: VaultItemType }) {
  const Icon = () => TYPE_ICONS[props.type] ?? UserSquare;
  return <Dynamic component={Icon()} class="h-4 w-4" />;
}

/**
 * Indicator of actual item state (weak/reused/old). Non-punitive:
 * healthy item shows no badge; only possible improvements flagged.
 */
export function StatusBadge(props: { status: ItemStatus }) {
  const { t } = useI18n();
  const label = (): string => {
    switch (props.status) {
      case "weak": {
        return t(msg`Weak`);
      }
      case "reused": {
        return t(msg`Reused`);
      }
      case "stale": {
        return t(msg`Old`);
      }
      default: {
        return "";
      }
    }
  };
  return (
    <Show when={props.status !== "ok"}>
      <span
        class="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[0.6875rem] font-medium text-warning"
        data-testid="vault-item-status"
        data-status={props.status}
      >
        <AlertTriangle class="h-3 w-3" aria-hidden="true" />
        {label()}
      </span>
    </Show>
  );
}

function BackHeader(props: { label: string; onBack: () => void }) {
  return (
    <div class="mb-4 flex items-center gap-2">
      <Button variant="ghost" size="icon" aria-label={props.label} onClick={() => props.onBack()}>
        <ArrowLeft class="h-4 w-4" />
      </Button>
      <h1 class="text-lg font-semibold">{props.label}</h1>
    </div>
  );
}

export function ItemDetail(props: {
  item: DecryptedVaultItem;
  /** Vault (personal or org) `item` belongs to — forwarded to `AttachmentsPanel` to scope mutations. */
  vaultId: string;
  onBack: () => void;
  onEdit: () => void;
  /** Rejects when the delete failed: the detail stays open and says so. */
  onDelete: () => Promise<void>;
  /** If passed, displays "Share" button (sealed sharing to member). */
  onShare?: () => void;
}) {
  const { t } = useI18n();
  const [revealed, setRevealed] = createSignal(false);
  const [deleting, setDeleting] = createSignal(false);
  const [deleteError, setDeleteError] = createSignal("");
  const plain = () => props.item.plain;

  async function handleDelete(): Promise<void> {
    setDeleteError("");
    setDeleting(true);
    try {
      await props.onDelete();
    } catch {
      setDeleteError(t(msg`Could not delete the item.`));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div class="flex flex-col gap-4">
      <BackHeader label={plain().name} onBack={() => props.onBack()} />

      <Show when={plain().fields.username}>
        <CopyField label={t(msg`Username`)} value={plain().fields.username ?? ""} />
      </Show>

      <Show when={plain().fields.password}>
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-muted-foreground">{t(msg`Password`)}</span>
          <div class="flex items-center gap-2">
            <code
              class="flex-1 break-all rounded-md bg-background px-3 py-2 font-mono text-sm"
              data-testid="detail-password"
            >
              {revealed() ? plain().fields.password : "••••••••••••"}
            </code>
            <Button
              variant="ghost"
              size="icon"
              aria-label={revealed() ? t(msg`Hide`) : t(msg`Reveal`)}
              onClick={() => setRevealed((v) => !v)}
              data-testid="detail-reveal"
            >
              <Show when={revealed()} fallback={<Eye class="h-4 w-4" />}>
                <EyeOff class="h-4 w-4" />
              </Show>
            </Button>
            <CopyButton value={plain().fields.password ?? ""} label={t(msg`Copy password`)} />
          </div>
        </div>
      </Show>

      <Show when={plain().fields.url}>
        <CopyField label={t(msg`Website`)} value={plain().fields.url ?? ""} />
      </Show>

      <Show when={plain().totpSecret}>
        <TotpField secret={plain().totpSecret ?? ""} />
      </Show>

      <Show when={plain().notes}>
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-muted-foreground">{t(msg`Notes`)}</span>
          <p class="whitespace-pre-wrap rounded-md bg-background px-3 py-2 text-sm">
            {plain().notes}
          </p>
        </div>
      </Show>

      <AttachmentsPanel itemId={props.item.id} vaultId={props.vaultId} />

      <div class="mt-2 flex gap-2">
        <Button variant="outline" onClick={() => props.onEdit()} data-testid="detail-edit">
          <Pencil class="h-4 w-4" /> {t(msg`Edit`)}
        </Button>
        <Show when={props.onShare}>
          <Button variant="outline" onClick={() => props.onShare?.()} data-testid="detail-share">
            <Share2 class="h-4 w-4" /> {t(msg`Share`)}
          </Button>
        </Show>
        <Button
          variant="destructive"
          disabled={deleting()}
          onClick={() => void handleDelete()}
          data-testid="detail-delete"
        >
          <Trash2 class="h-4 w-4" /> {t(msg`Delete`)}
        </Button>
      </div>
      <Show when={deleteError()}>
        <p class="text-sm text-destructive" role="alert" data-testid="detail-delete-error">
          {deleteError()}
        </p>
      </Show>
    </div>
  );
}

/**
 * Live TOTP code (RFC 6238): recalculates code every second from
 * item's encrypted seed and displays period countdown. Calculation is
 * 100% local (seed never leaves client in plaintext).
 */
function TotpField(props: { secret: string }) {
  const { t } = useI18n();
  const [code, setCode] = createSignal("");
  const [remaining, setRemaining] = createSignal(30);
  const [invalid, setInvalid] = createSignal(false);

  async function refresh(): Promise<void> {
    try {
      setCode(await crypto.generateTotp(props.secret));
      setRemaining(crypto.totpTimeRemaining());
      setInvalid(false);
    } catch {
      setInvalid(true);
    }
  }

  createEffect(() => {
    // Track props.secret: if item changes, code recalculates immediately.
    void refresh();
    const timer = setInterval(() => void refresh(), 1000);
    onCleanup(() => clearInterval(timer));
  });

  const pretty = () => formatTotpCode(code());

  return (
    <div class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-muted-foreground">{t(msg`Authenticator (TOTP)`)}</span>
      <Show
        when={!invalid()}
        fallback={
          <span class="rounded-md bg-background px-3 py-2 text-sm text-destructive" role="alert">
            {t(msg`Invalid authenticator secret`)}
          </span>
        }
      >
        <div class="flex items-center gap-2">
          <code
            class="flex-1 rounded-md bg-background px-3 py-2 font-mono text-lg tracking-widest tabular-nums"
            data-testid="detail-totp"
          >
            {pretty()}
          </code>
          <span
            class="w-8 shrink-0 text-center text-xs font-medium text-muted-foreground tabular-nums"
            aria-label={t(msg`Seconds remaining`)}
            data-testid="detail-totp-remaining"
          >
            {remaining()}s
          </span>
          <CopyButton value={code()} label={t(msg`Copy code`)} />
        </div>
      </Show>
    </div>
  );
}

function CopyField(props: { label: string; value: string }) {
  return (
    <div class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-muted-foreground">{props.label}</span>
      <div class="flex items-center gap-2">
        <span class="flex-1 break-all rounded-md bg-background px-3 py-2 text-sm">
          {props.value}
        </span>
        <CopyButton value={props.value} label={props.label} />
      </div>
    </div>
  );
}

function CopyButton(props: { value: string; label: string }) {
  const [copied, setCopied] = createSignal(false);
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={props.label}
      onClick={() => {
        void navigator.clipboard.writeText(props.value);
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
      }}
    >
      <Show when={copied()} fallback={<Copy class="h-4 w-4" />}>
        <Check class="h-4 w-4 text-success" />
      </Show>
    </Button>
  );
}
